import React from "react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Wifi, WifiOff } from "lucide-react";
import { useNetworkState } from "@uidotdev/usehooks";
import useUserToken from "@/hook/useUserToken";
import { S3_BUCKET_LINK } from "@/components/constants";

interface DocumentPreviewNavbarProps {
  fileName?: string;
  downloadUrl?: string;
  onBack?: () => void;
  onOpenInApp?: () => void;
}

const DocumentPreviewNavbar: React.FC<DocumentPreviewNavbarProps> = ({
  onBack,
}) => {
  const user = useUserToken();
  const userData = user?.data;
  const network = useNetworkState();

  const getNetworkQuality = () => {
    if (!network?.online)
      return {
        status: "ຂາດການເຊື່ອມຕໍ່ອິນເຕີເນັດ",
        color: "text-red-500",
        icon: WifiOff,
      };

    if (network.effectiveType === "4g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວສູງ",
        color: "text-green-500",
        icon: Wifi,
      };
    }
    if (network.effectiveType === "3g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວປານກາງ",
        color: "text-yellow-500",
        icon: Wifi,
      };
    }
    if (network.effectiveType === "2g" || network.effectiveType === "slow-2g") {
      return {
        status: "ເຊື່ອມຕໍ່ດ້ວຍຄວາມໄວຕ່ຳ",
        color: "text-orange-500",
        icon: Wifi,
      };
    }

    return {
      status: "ເຊື່ອມຕໍ່ອິນເຕີເນັດແລ້ວ",
      color: "text-green-500",
      icon: Wifi,
    };
  };

  const networkQuality = getNetworkQuality();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Default behavior: close the tab or go back
      if (window.history.length > 1) {
        window.history.back();
      } else {
        window.close();
      }
    }
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b bg-white shadow-sm">
      <div className="flex items-center justify-between px-6 py-3">
        {/* Left Section */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <img src="/soei_logo.png" alt="SOE Logo" className="w-8 h-8" />
            <div className="hidden md:block">
              <h1 className="text-sm font-bold text-primary">
                ລະບົບ ຄຸ້ມຄອງວິສາຫະກິດລັດລົງທຶນ
              </h1>
              <p className="text-xs text-gray-600">
                ກົມຄຸ້ມຄອງລັດວິສາຫະກິດລັດລົງທຶນ ແລະ ການປະກັນໄພ
              </p>
            </div>
          </div>

          <div className="h-6 w-px bg-gray-300 hidden md:block" />

          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBack}
              className="flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">ກັບຄືນ</span>
            </Button>
          </div>
        </div>

        {/* Right Section */}
        <div className="flex items-center gap-4">
          {/* Network Status */}
          <Badge
            variant="secondary"
            className="hidden lg:flex gap-2 items-center px-3 py-1"
          >
            <div className={`flex gap-2 items-center ${networkQuality.color}`}>
              <networkQuality.icon className="w-4 h-4" />
              <span className="text-xs">{networkQuality.status}</span>
              {network?.downlink && (
                <span className="text-xs">
                  ({network.downlink.toFixed(1)} Mbps)
                </span>
              )}
            </div>
          </Badge>

          {/* User Info */}
          {userData && (
            <div className="flex items-center gap-2">
              <Avatar className="w-8 h-8">
                <AvatarImage
                  className="object-cover"
                  src={S3_BUCKET_LINK + userData?.profileUrl}
                />
                <AvatarFallback className="text-xs">
                  {userData?.firstName?.[0]}
                </AvatarFallback>
              </Avatar>
              <div className="hidden md:block">
                <p className="text-sm font-medium">
                  {userData?.firstName} {userData?.lastName}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default DocumentPreviewNavbar;
